import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';

export interface CookiePreferences {
  essential: boolean; // Always true - required for site to function (auth, language, theme)
  preferences: boolean; // User preferences (saved filters, notification settings, cross-tab sync)
}

interface CookieConsentContextType {
  preferences: CookiePreferences;
  showBanner: boolean;
  acceptAll: () => void;
  declineAll: () => void;
  savePreferences: (prefs: CookiePreferences) => void;
  openSettings: () => void;
  closeSettings: () => void;
  isSettingsOpen: boolean;
}

const CookieConsentContext = createContext<
  CookieConsentContextType | undefined
>(undefined);

const CONSENT_STORAGE_KEY = 'cookie_consent';
const PREFERENCES_STORAGE_KEY = 'cookie_preferences';

const DEFAULT_PREFERENCES: CookiePreferences = {
  essential: true, // Always true - can't be disabled (authentication, language, theme)
  preferences: false, // User preferences (saved filters, notifications, sync)
};

export const CookieConsentProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [preferences, setPreferences] =
    useState<CookiePreferences>(DEFAULT_PREFERENCES);
  const [showBanner, setShowBanner] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // Load consent status from localStorage on mount
  useEffect(() => {
    try {
      const storedConsent = localStorage.getItem(CONSENT_STORAGE_KEY);
      const storedPrefs = localStorage.getItem(PREFERENCES_STORAGE_KEY);

      if (
        storedConsent &&
        (storedConsent === 'accepted' ||
          storedConsent === 'declined' ||
          storedConsent === 'configured')
      ) {
        if (storedPrefs) {
          try {
            const parsed = JSON.parse(storedPrefs);
            setPreferences({ ...DEFAULT_PREFERENCES, ...parsed });
          } catch {
            // Invalid preferences, use defaults
          }
        }
      } else {
        // No consent given yet - show banner
        setShowBanner(true);
      }
    } catch (error) {
      console.error('Error loading cookie consent:', error);
      // If we can't read localStorage, show banner to ask for consent
      setShowBanner(true);
    }
  }, []);

  const acceptAll = () => {
    const allAccepted: CookiePreferences = {
      essential: true,
      preferences: true,
    };

    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, 'accepted');
      localStorage.setItem(
        PREFERENCES_STORAGE_KEY,
        JSON.stringify(allAccepted)
      );
      setPreferences(allAccepted);
      setShowBanner(false);
      setIsSettingsOpen(false);
    } catch (error) {
      console.error('Error saving cookie consent:', error);
    }
  };

  const declineAll = () => {
    const onlyEssential: CookiePreferences = {
      essential: true,
      preferences: false,
    };

    try {
      // Store consent preference even when declined (GDPR requirement)
      localStorage.setItem(CONSENT_STORAGE_KEY, 'declined');
      localStorage.setItem(
        PREFERENCES_STORAGE_KEY,
        JSON.stringify(onlyEssential)
      );
      setPreferences(onlyEssential);
      setShowBanner(false);
      setIsSettingsOpen(false);
    } catch (error) {
      console.error('Error saving cookie consent:', error);
    }
  };

  const savePreferences = (prefs: CookiePreferences) => {
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, 'configured');
      localStorage.setItem(PREFERENCES_STORAGE_KEY, JSON.stringify(prefs));
      setPreferences(prefs);
      setShowBanner(false);
      setIsSettingsOpen(false);
    } catch (error) {
      console.error('Error saving cookie preferences:', error);
    }
  };

  const openSettings = () => {
    setIsSettingsOpen(true);
  };

  const closeSettings = () => {
    setIsSettingsOpen(false);
  };

  return (
    <CookieConsentContext.Provider
      value={{
        preferences,
        showBanner,
        acceptAll,
        declineAll,
        savePreferences,
        openSettings,
        closeSettings,
        isSettingsOpen,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error(
      'useCookieConsent must be used within a CookieConsentProvider'
    );
  }
  return context;
};
